// src/utils/parseSceneReference.ts
/**
 * Scene Reference Parser
 * Parses user messages to identify scene number references
 */

import type { ISceneReference, ISceneParseResult } from '@/@types/globalChat';

// Number words for parsing "third scene", "first scene", etc.
const NUMBER_WORDS: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
};

/**
 * Parse a user message to extract scene number reference
 */
export function parseSceneReference(
  message: string,
  availableScenes: ISceneReference[]
): ISceneParseResult {
  const lowerMessage = message.toLowerCase();

  // Pattern 0: "the last scene", "last scene", "final scene"
  if (lowerMessage.includes('last scene') || lowerMessage.includes('final scene')) {
    if (availableScenes.length > 0) {
      const lastScene = availableScenes.reduce((max, scene) =>
        scene.sceneNumber > max.sceneNumber ? scene : max
      );
      return {
        sceneNumber: lastScene.sceneNumber,
        sceneId: lastScene.sceneId,
        confidence: 'exact',
      };
    }
  }

  // Pattern 1: "scene 3", "scene #3", "scene number 3", "scene no. 3"
  const sceneNumberMatch = lowerMessage.match(/scene\s*(?:#|number|no\.?)?\s*(\d+)/i);
  if (sceneNumberMatch) {
    const sceneNumber = parseInt(sceneNumberMatch[1], 10);
    return findSceneByNumber(sceneNumber, availableScenes);
  }

  // Pattern 2: "3rd scene", "1st scene", etc.
  const ordinalMatch = lowerMessage.match(/(\d+)(?:st|nd|rd|th)\s+scene/i);
  if (ordinalMatch) {
    const sceneNumber = parseInt(ordinalMatch[1], 10);
    return findSceneByNumber(sceneNumber, availableScenes);
  }

  // Pattern 3: "the third scene", "first scene", etc.
  for (const [word, num] of Object.entries(NUMBER_WORDS)) {
    if (lowerMessage.includes(`${word} scene`) || lowerMessage.includes(`the ${word}`)) {
      return findSceneByNumber(num, availableScenes);
    }
  }

  // Pattern 4: "the exterior scene", "interior scene" (match by scene type)
  const sceneTypes = ['exterior', 'interior', 'pool', 'kitchen', 'bedroom', 'bathroom', 'living', 'garden', 'balcony', 'view'];
  for (const type of sceneTypes) {
    if (lowerMessage.includes(`${type} scene`) || lowerMessage.includes(`the ${type}`)) {
      const matches = availableScenes.filter(
        (s) => s.sceneType.toLowerCase().includes(type)
      );

      if (matches.length === 1) {
        return {
          sceneNumber: matches[0].sceneNumber,
          sceneId: matches[0].sceneId,
          confidence: 'exact',
        };
      }

      if (matches.length > 1) {
        return {
          sceneNumber: null,
          sceneId: null,
          confidence: 'ambiguous',
          ambiguousMatches: matches,
        };
      }
    }
  }

  // No scene reference found
  return {
    sceneNumber: null,
    sceneId: null,
    confidence: 'none',
  };
}

/**
 * Find scene by number in available scenes
 */
function findSceneByNumber(
  sceneNumber: number,
  availableScenes: ISceneReference[]
): ISceneParseResult {
  const scene = availableScenes.find((s) => s.sceneNumber === sceneNumber);

  if (scene) {
    return {
      sceneNumber: scene.sceneNumber,
      sceneId: scene.sceneId,
      confidence: 'exact',
    };
  }

  // Scene number out of range
  return {
    sceneNumber: sceneNumber,
    sceneId: null,
    confidence: 'none',
  };
}

/**
 * Build clarification message when scene not specified
 */
export function buildClarificationMessage(availableScenes: ISceneReference[]): string {
  if (availableScenes.length === 0) {
    return "I don't see any scenes to edit on this page. Please navigate to a Property Video with scenes.";
  }

  const sceneCount = availableScenes.length;
  return `Which scene would you like me to edit? Please specify a scene number (1-${sceneCount}).`;
}

/**
 * Build ambiguous match message
 */
export function buildAmbiguousMessage(matches: ISceneReference[]): string {
  const options = matches
    .map((m) => `Scene ${m.sceneNumber}: ${m.sceneType}`)
    .join(', ');

  return `I found multiple matching scenes: ${options}. Which one did you mean?`;
}
